import {
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Alert,
  AlertIcon,
  ModalProps,
  ButtonProps,
} from '@chakra-ui/react';

type BaseModalProps = ModalProps & {
  header?: React.ReactNode;
  showWarning?: boolean;
  warningMessage?: string;
  onSubmit?: ButtonProps['onClick'];
  submitText?: string;
  isLoading?: boolean;
};

const BaseModal: React.FC<BaseModalProps> = ({
  header,
  showWarning,
  warningMessage,
  onSubmit: onConfirm,
  children,
  isLoading,
  submitText = 'save',
  ...props
}) => {
  return (
    <Modal size={'xl'} isCentered {...props}>
      <ModalOverlay bg="blackAlpha.200" backdropFilter="blur(4px)" />
      <ModalContent
        maxH="90vh"
        overflowY="auto"
        borderWidth="0.5px"
        borderColor="#D4D4D4"
        mx={4}
      >
        <ModalHeader
          fontSize={'sm'}
          color={'whiteAlpha.900'}
          background={'purple.500'}
          borderTopRadius="md"
          py={3}
          px={4}
        >
          {header}
        </ModalHeader>
        <ModalCloseButton
          color={'whiteAlpha.900'}
          my={1}
          zIndex={2}
          sx={{
            _hover: {
              bg: 'transparent',
              color: 'white',
            },
          }}
        />
        {showWarning ? (
          <Alert
            status="warning"
            fontWeight={600}
            fontSize={'2xs'}
            color={'orange.700'}
            px={6}
          >
            <AlertIcon />
            {warningMessage}
          </Alert>
        ) : null}
        <ModalBody pb={6} gap={2}>
          {children}
        </ModalBody>

        {!!onConfirm ? (
          <ModalFooter gap={2}>
            <Button
              borderRadius="6px"
              bgGradient="linear(to-r, #6535EB, #9F3198)"
              color="white"
              height={{ base: '26px', md: '34px' }}
              px={{ base: 3, md: 5 }}
              fontSize={{ base: '10px', md: '12px' }}
              fontWeight="500"
              whiteSpace="nowrap"
              transition="all 0.2s ease"
              _hover={{
                transform: 'translateY(-1px)',
                bgGradient: 'linear(to-r, #5a2fe0, #8a2a85)',
              }}
              _active={{
                transform: 'scale(0.98)',
              }}
              mr={3}
              size="sm"
              onClick={onConfirm}
              isLoading={isLoading}
            >
              {submitText}
            </Button>
            <Button
              size="sm"
              variant="outline"
              color="gray.500"
              borderWidth="0.5px"
              borderColor="#D4D4D4"
              _hover={{ bg: 'gray.100', color: 'gray.700', borderColor: '#D4D4D4' }}
              borderRadius="lg"
              onClick={props.onClose}
              isLoading={isLoading}
            >
              Cancel
            </Button>
          </ModalFooter>
        ) : null}
      </ModalContent>
    </Modal>
  );
};

export default BaseModal;